/*
  Image Banner Blocks Carousel — minimal companion JS.

  Only job in v1: respect the user's `prefers-reduced-motion` setting at
  carousel init AND when it changes mid-session. Hero's CSS-only approach of
  setting `--swiper-autoplay-delay: 0` does not actually halt autoplay (Swiper
  interprets 0 as "advance instantly"), so this needs a JS hook.

  Keeps registration guarded, cleans up listeners on disconnect, no globals.
*/
if (!customElements.get('ibbc-carousel')) {
    customElements.define(
        'ibbc-carousel',
        class IBBCCarousel extends HTMLElement {
            connectedCallback() {
                this.swiperEl = this.querySelector('swiper-container');
                if (!this.swiperEl) return;

                // Set up reduced-motion infrastructure FIRST. waitForSwiperInit
                // can call onSwiperReady synchronously (when Swiper has already
                // booted), and onSwiperReady calls applyReducedMotion — which
                // expects reduceMotionQuery to exist.
                this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
                this.onReduceMotionChange = this.onReduceMotionChange.bind(this);

                // Pre-init pass: if reduced-motion is on AND Swiper hasn't booted
                // yet, removing the `autoplay` attribute here prevents Swiper
                // from picking it up. If Swiper HAS booted, this also calls
                // sw.autoplay.stop().
                this.applyReducedMotion();

                // Wait for Swiper boot, then re-apply reduced-motion to cover
                // the race where Swiper init was deferred past our connect AND
                // cached the autoplay config from the original attribute.
                this.waitForSwiperInit();

                this.reduceMotionQuery.addEventListener('change', this.onReduceMotionChange);
            }

            disconnectedCallback() {
                if (this.reduceMotionQuery && this.onReduceMotionChange) {
                    this.reduceMotionQuery.removeEventListener('change', this.onReduceMotionChange);
                }
            }

            waitForSwiperInit() {
                // Swiper Element exposes its instance via the `swiper` property
                // on the custom element once initialized. Poll briefly with rAF;
                // mirrors the pattern used by `product--media-gallery.js`.
                if (this.swiperEl.swiper) {
                    this.onSwiperReady();
                    return;
                }
                let frames = 0;
                const check = () => {
                    if (!this.isConnected) return;
                    if (this.swiperEl.swiper) {
                        this.onSwiperReady();
                        return;
                    }
                    if (++frames < 60) requestAnimationFrame(check);
                    else this.classList.add('ibbc-initialized');
                    // After 60 frames (~1s) we give up and release the clamp
                    // anyway — better to show a flash than a permanently
                    // single-slide-height-clamped container if Swiper didn't boot.
                };
                requestAnimationFrame(check);
            }

            onSwiperReady() {
                this.classList.add('ibbc-initialized');
                // Re-apply reduced-motion now that Swiper is alive. Handles the
                // page-load-with-reduced-motion-already-on race: at connect,
                // Swiper hadn't yet exposed `.swiper`, so applyReducedMotion
                // skipped the autoplay.stop() call. Removing the `autoplay`
                // attribute alone isn't enough — Swiper appears to cache its
                // config from the initial attributes, so we need stop() to land
                // on the running instance.
                this.applyReducedMotion();
            }

            onReduceMotionChange() {
                this.applyReducedMotion();
            }

            applyReducedMotion() {
                if (!this.swiperEl) return;

                if (this.reduceMotionQuery.matches) {
                    // Strip the attribute so Swiper doesn't auto-restart on re-init,
                    // and stop the running autoplay instance if Swiper has already booted.
                    this.swiperEl.removeAttribute('autoplay');
                    const sw = this.swiperEl.swiper;
                    if (sw && sw.autoplay && typeof sw.autoplay.stop === 'function') {
                        sw.autoplay.stop();
                    }
                }
            }
        }
    );
}
